import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useUIStore, useCollectionStore, useModalStore, useChatStore, useOnboardingStore, useAuthStore, useToastStore, FILTER_CONFIGS } from '@/store';
import { useRouter } from 'expo-router';
import { useCurrentRoute } from '@/hooks/useCurrentRoute';
import useRequireAuth from '@/hooks/useRequireAuth';
import SidebarListContainer from './SidebarListContainer';
import SidebarListItemCollection from './SidebarListItemCollection';
import SidebarListItemPlaceholder from '@/components/Placeholders/SidebarListItemPlaceholder';
import SidebarListItemLayout from '@/components/Sidebar/SidebarListItemLayout';
import SidebarCollectionOrderEditor from './SidebarCollectionOrderEditor';
import { buildFolderTree, sortFolderTree, flattenFolderTree } from '@/constants/utils/folder_tree';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const COLLECTIONS_LIMIT = 10;

function SidebarCollectionsSection() {
  const { t } = useTranslation();
  const router = useRouter();
  const route = useCurrentRoute();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(COLLECTIONS_LIMIT);
  const [isReordering, setIsReordering] = useState(false);
  const loadingRef = useRef(false);
  const sessionRef = useRef(null);
  const { colors } = useTheme();
  const userId = useAuthStore(state => state.user?.id);
  const collectionOrder = useUIStore(state => state.sidebarCollectionOrderByUser?.[userId]);
  const setCollectionOrder = useUIStore(state => state.setSidebarCollectionOrder);
  const addToast = useToastStore(state => state.actions.addToast);

  useEffect(() => {
    const session = {};
    sessionRef.current = session;
    loadingRef.current = false;
    setIsLoadingMore(false);
    setIsReordering(false);
    setVisibleCount(COLLECTIONS_LIMIT);
    return () => { sessionRef.current = null; };
  }, [userId]);

  // Collections state
  const collections = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const sortOrder = useCollectionStore(state => state.sortOrder);
  const contentSortOrders = useCollectionStore(state => state.contentSortOrders);
  const pagination = useCollectionStore(state => state.pagination);
  const isLoading = useCollectionStore(state => state.isLoading.fetch);
  const handleFilterSortClick = useCollectionStore(state => state.handleFilterSortClick);
  const fetchCollections = useCollectionStore(state => state.fetchCollections);
  const { openModal } = useModalStore(state => state.actions);
  const { registerTarget, unregisterTarget } = useOnboardingStore(state => state.actions);
  const requireAuth = useRequireAuth();
  const sectionRef = useRef(null);

  useEffect(() => {
    if (sectionRef.current) {
        registerTarget(5, sectionRef);
    }
    return () => unregisterTarget(5);
  }, [registerTarget, unregisterTarget]);

  // Get chats from store
  const chats = useChatStore(state => state.chats);

  // UI State
  const collapsedSections = useUIStore(state => state.collapsedSections);
  const toggleSection = useUIStore(state => state.toggleSection);

  // Memoize the isCollectionRoute function to prevent recreation
  const isCollectionRoute = useCallback((id) => route.isCollection(id), [route]);
  const isLibraryChat = useCallback((id) => route.isLibraryChat(id), [route]);

  // Memoize the collection items to prevent unnecessary re-renders
  const displayedItems = useMemo(() => {
    return collectionIds
      .map(id => {
        const item = collections[id];
        if (!item) return null;

        // Use this collection's chat preference, independently of folder order.
        const chatSortOrder = contentSortOrders[item.id]?.chats ?? 'newest';
        const collectionChats = (item.conversationIds || [])
          .map((conversationId) => {
            const chat = chats.find(c => c?.id === conversationId);
            if (!chat) return null;
            return {
              ...chat,
              isActive: isLibraryChat(chat.id)
            };
          })
          .filter(Boolean)
          .sort((a, b) => {
            if (chatSortOrder === 'a-z') return (a.title || '').localeCompare(b.title || '');
            if (chatSortOrder === 'z-a') return (b.title || '').localeCompare(a.title || '');
            const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
            const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
            return chatSortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
          });

        // Determine most recent activity timestamp using reliable fields
        const latestChat = collectionChats.length > 0 
          ? collectionChats.reduce((latest, current) => {
              const currentTime = new Date(current.updatedAt || current.createdAt || 0).getTime();
              const latestTime = new Date(latest.updatedAt || latest.createdAt || 0).getTime();
              return currentTime > latestTime ? current : latest;
            }, collectionChats[0])
          : null;
        const lastActivityMs = latestChat ? new Date(latestChat.updatedAt || latestChat.createdAt || 0).getTime() : 0;

        return {
          id: item.id,
          title: item.title,
          icon: item.icon,
          href: `/collections/${item.id}`,
          isArchived: item?.flags?.isArchived,
          isActive: isCollectionRoute(item.id),
          chatCount: collectionChats.length,
          chats: collectionChats,
          lastActivityMs,
          parentId: item.parentId || null,
        };
      })
      .filter(Boolean);
  }, [collections, collectionIds, isCollectionRoute, isLibraryChat, chats, contentSortOrders]);

  // Sort displayed items based on sort order, then nest by parentId
  const sortedItems = useMemo(() => {
    const ranks = new Map((collectionOrder || []).map((id, index) => [id, index]));
    const compare = (a, b) => {
      const rankDifference = (ranks.get(a.id) ?? Infinity) - (ranks.get(b.id) ?? Infinity);
      if (rankDifference) return rankDifference;
      switch (sortOrder) {
        case 'newest':
          return (b.lastActivityMs || 0) - (a.lastActivityMs || 0);
        case 'oldest':
          return (a.lastActivityMs || 0) - (b.lastActivityMs || 0);
        case 'a-z':
          return a.title.localeCompare(b.title);
        case 'z-a':
          return b.title.localeCompare(a.title);
        default:
          return 0;
      }
    };
    return sortFolderTree(buildFolderTree(displayedItems), compare);
  }, [displayedItems, sortOrder, collectionOrder]);

  const handleCollectionsLabelClick = useCallback((labelId) => {
    if (FILTER_CONFIGS.collections.sort.some(option => option.id === labelId)) {
      setCollectionOrder(userId, []);
      setIsReordering(false);
    }
    if (labelId === null) {
      handleFilterSortClick(null);
      router.push('/files');
    } else {
      handleFilterSortClick(labelId);
    }
  }, [router, handleFilterSortClick, setCollectionOrder, userId]);

  const getCurrentCollectionsPath = useCallback(() => {
    if (collectionOrder?.length) return [{ id: 'custom', title: 'Custom' }];
    const path = [];
    if (sortOrder) {
      const sortLabel = FILTER_CONFIGS.collections.sort
        .find(l => l.id === sortOrder);
      if (sortLabel) path.push(sortLabel);
    }
    return path;
  }, [sortOrder, collectionOrder]);

  const loadCollections = useCallback(async (reorder = false) => {
    if (loadingRef.current || useCollectionStore.getState().isLoading.fetch) return;
    const session = sessionRef.current;
    const targetCount = visibleCount + COLLECTIONS_LIMIT;
    loadingRef.current = true;
    setIsLoadingMore(true);
    try {
      // Reveal cached roots first. Nested collections stay with their parent
      // and do not consume the sidebar's ten top-level slots.
      let state = useCollectionStore.getState();
      const rootCount = () => buildFolderTree(state.collectionIds.map(id => state.collections[id]).filter(Boolean)).length;
      while (state.pagination.hasMore && (reorder || rootCount() < targetCount)) {
        const previousPage = state.pagination.page;
        await fetchCollections(previousPage + 1);
        if (sessionRef.current !== session || useAuthStore.getState().user?.id !== userId) return;
        state = useCollectionStore.getState();
        if (state.errors.fetch || (state.pagination.hasMore && state.pagination.page <= previousPage)) {
          throw new Error(state.errors.fetch || 'Could not load more collections');
        }
      }
      if (reorder) setIsReordering(true);
      else setVisibleCount(targetCount);
    } catch (error) {
      if (sessionRef.current === session) addToast(error.message || 'Could not load collections', 'error');
    } finally {
      if (sessionRef.current === session) {
        loadingRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [visibleCount, fetchCollections, addToast, userId]);

  const handleReorder = useCallback((ids) => {
    // Replace this sibling group's slots while retaining every other folder's order.
    const group = new Set(ids);
    let index = 0;
    const order = flattenFolderTree(sortedItems).map(item => group.has(item.id) ? ids[index++] : item.id);
    setCollectionOrder(userId, order);
  }, [sortedItems, setCollectionOrder, userId]);

  const handleCreateCollection = useCallback(() => {
    requireAuth(
      () => openModal('CreateCollectionModal'),
      {
        source: 'sidebar_create_collection',
        title: 'Sign in to create a Collection',
        subtitle: 'Collections group files and chats so Gab can pull from the same context across every conversation.',
        icon: 'folder-plus',
      },
    );
  }, [openModal, requireAuth]);

  return (
    <>
      <SidebarListContainer
        containerRef={sectionRef}
        title={t('navigation.collections')}
        labels={[
          { id: 'sort', title: 'Sort', children: FILTER_CONFIGS.collections.sort }
        ]}
        currentLabelPath={getCurrentCollectionsPath()}
        isCollapsed={collapsedSections.collections}
        onToggle={() => toggleSection('collections')}
        onLabelClick={handleCollectionsLabelClick}
        stickyIndexTop={1}
        keepActionsVisible={isReordering}
        headerAction={userId && !collapsedSections.collections && (collectionIds.length > 1 || pagination.hasMore) ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={isReordering ? 'Done reordering collections' : 'Reorder collections'}
            disabled={isLoadingMore || isLoading}
            onPress={event => {
              event?.stopPropagation?.();
              if (isReordering) setIsReordering(false);
              else void loadCollections(true);
            }}
            style={{ paddingHorizontal: isReordering ? 10 : 6, paddingVertical: 4, borderRadius: 6,
              backgroundColor: isReordering ? colors.solid_color_secondary : undefined }}
          >
            {isReordering ? <Text size="s" weight="semibold">Done</Text> : <Icon name="gear" size={14} color={colors.text_color_secondary} />}
          </Pressable>
        ) : null}
      />
      {!collapsedSections.collections && (
        <>
          <SidebarListItemLayout
            noEllipsis
            title='New collection'
            icon="folder-plus"
            onPress={handleCreateCollection}
          />
          {isLoading && !sortedItems.length ? (
            // Show 4 placeholder items while loading
            Array.from({ length: 4 }).map((_, index) => (
              <SidebarListItemPlaceholder key={`placeholder-${index}`} />
            ))
          ) : (
            <>
              {isReordering ? <SidebarCollectionOrderEditor items={sortedItems} onReorder={handleReorder} /> : sortedItems.slice(0, visibleCount).map((item, index) => (
                <SidebarListItemCollection
                  key={`sidebar-list-item-collection-${item?.id || index}`}
                  id={item.id}
                  title={item.title}
                  href={item.href}
                  isArchived={item.isArchived}
                  isActive={item.isActive}
                  chatCount={item.chatCount}
                  chats={item.chats}
                  childCollections={item.children || []}
                />
              ))}
              {!isReordering && (sortedItems.length > visibleCount || pagination.hasMore) && (
                <SidebarListItemLayout
                  noEllipsis
                  title={isLoadingMore ? 'Loading…' : 'See more'}
                  accessibilityLabel="See more collections"
                  disabled={isLoadingMore || isLoading}
                  onPress={() => loadCollections()}
                />
              )}
              {!isReordering && visibleCount > COLLECTIONS_LIMIT && sortedItems.length > COLLECTIONS_LIMIT && (
                <SidebarListItemLayout noEllipsis title="See less" accessibilityLabel="See fewer collections"
                  onPress={() => setVisibleCount(COLLECTIONS_LIMIT)} />
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

export default React.memo(SidebarCollectionsSection);
